import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit} from '@angular/core';
import {NbDialogRef} from '@nebular/theme';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {Observable} from 'rxjs';
import {
    DocumentAssetImage,
    DocumentFunctionType,
    DocumentFunctionTypeLabels,
    FileFormatType,
    FileFormatTypeLabels,
} from '@core/interfaces/common/document';
import {delay, take, takeUntil} from 'rxjs/operators';
import {Unsubscribable} from '@core/interfaces/unsubscribable';
import {LocalDataPreparationFile} from '../../../../pages/data-preparation/steps/upload-file/upload-file.component';
import {GeneralOption} from '@core/interfaces/system/system-common';
import {PagesStore} from '@store/config/pages.store';
import {Page} from '@core/interfaces/common/pages';

export type TagOptionsCallback = (searchString?: string) => Observable<string[]>;

@Component({
    selector: 'ngx-upload-file-dialog',
    templateUrl: './upload-file-dialog.component.html',
    styleUrls: ['./upload-file-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UploadFileDialogComponent extends Unsubscribable implements OnInit {
    @Input() acceptedFileTypes: string[];
    @Input() file?: DocumentAssetImage | any;
    @Input() getTagOptionsCallback: TagOptionsCallback;

    public customTagOptions: FormControl = this.fb.control(['']);
    public tagInputFormControl: FormControl = new FormControl('');

    showDocumentFunction: boolean = false;

    public fileForm: FormGroup = this.fb.group({
        fileName: [
            null,
            [Validators.required, Validators.maxLength(255), Validators.pattern(/^[a-zA-Z0-9!\-_.*'() ]+$/)],
        ],
        tag: [null, [Validators.required, Validators.maxLength(100)]],
        documentFunctionType: [DocumentFunctionType.NONE, [Validators.required, Validators.maxLength(100)]],
        description: [null, [Validators.maxLength(255)]],
        url: [null, Validators.required],
        file: [null, Validators.required],
        size: [null],
    });

    public saveBtnPressed = false;

    public FileFormatTypeLabels = FileFormatTypeLabels;

    public documentFunctionTypeOptions: GeneralOption[] = Object.keys(DocumentFunctionTypeLabels).map((key) => {
        return {value: key, label: DocumentFunctionTypeLabels[key]};
    });

    constructor(
        protected ref: NbDialogRef<UploadFileDialogComponent>,
        private fb: FormBuilder,
        private changeDetectorRef: ChangeDetectorRef,
        private pagesStore: PagesStore,
    ) {
        super();
    }

    ngOnInit(): void {
        this.tagInputFormControl.valueChanges
            .pipe(takeUntil(this.unsubscribe$), delay(500))
            .subscribe((value: string) => {
                this.fileForm.get('tag').setValue(value);
            });

        if (this.file) {
            if (this.file.fileName.lastIndexOf('.') != -1) {
                this.file.fileName = this.file.fileName.substring(0, this.file.fileName.lastIndexOf('.'));
            }

            this.fileForm.patchValue({
                ...this.file,
                file: {
                    fileFormat: this.file.fileFormat,
                    fileType: this.file.fileType,
                    fileName: this.file.fileName,
                    size: this.file.size,
                },
            });

            this.tagInputFormControl.setValue(this.file.tag);
        }
        if (this.getTagOptionsCallback) {
            this.updateTagOptions('');
        } else {
            this.fileForm.get('tag')?.clearValidators();
            this.fileForm.get('tag')?.updateValueAndValidity();
        }

        this.pagesStore.activeFeature$.pipe(take(1)).subscribe((page: Page) => {
            this.showDocumentFunction = ['asset-registry', 'geospatial-viewer'].includes(page.id);

            if (page.id === 'geospatial-viewer') {
                this.fileForm
                    .get('documentFunctionType')
                    .setValue(DocumentFunctionType.LAYOUT_DIAGRAM, {emitEvent: false});
                this.fileForm.get('documentFunctionType').disable();
            }
        });
    }

    public unstageImage(): void {
        this.fileForm.patchValue({file: null});
        this.fileForm.patchValue({url: null});
    }

    public stageImage(file): void {
        this.fileForm.patchValue({
            file: file,
        });
    }

    public onFileAdded(file: LocalDataPreparationFile): void {
        const fr = new FileReader();
        fr.readAsDataURL(file.file);
        fr.onload = (e) => {
            this.fileForm.patchValue({url: e.target.result});

            this.changeDetectorRef.detectChanges();
        };

        if (file.name.lastIndexOf('.') != -1) {
            file.name = file.name.substring(0, file.name.lastIndexOf('.'));
        }

        this.stageImage({
            ...file,
            fileName: file.name,
            documentType: 'ASSET',
        });

        this.fileForm.patchValue({
            fileName: file.name,
            size: (file.size / 1048576).toFixed(2),
        });
    }

    public cancel(): void {
        this.ref.close();
    }

    public save(): void {
        if (this.fileForm.invalid) {
            this.saveBtnPressed = true;
            this.fileForm.markAllAsTouched();
            return;
        }
        if (this.getTagOptionsCallback) {
            this.fileForm.get('tag').setValue(this.fileForm.get('tag').value.trim(), {emitEvent: false});
        }
        this.ref.close(this.fileForm.getRawValue());
    }

    public documentIsImage(fileFormat: FileFormatType): boolean {
        switch (fileFormat) {
            case FileFormatType.JPG:
            case FileFormatType.JPEG:
            case FileFormatType.PNG:
                return true;
            default:
                return false;
        }
    }

    public documentIsData(fileFormat: FileFormatType): boolean {
        switch (fileFormat) {
            case FileFormatType.CSV:
            case FileFormatType.MS_EXCEL:
            case FileFormatType.PDF:
                return true;
            default:
                return false;
        }
    }

    public onSearch(searchString: string): void {
        this.updateTag(searchString);
        this.updateTagOptions(searchString);
    }

    private updateTagOptions(searchString: string): void {
        if (this.fileForm.get('tag').valid) {
            this.getTagOptionsCallback(searchString)
                .pipe(take(1)) //in case it's not an API call, we need to trigger it only once
                .subscribe((options) => {
                    this.customTagOptions.setValue(options);
                });
        }
    }

    private updateTag(inputString: string): void {
        this.fileForm.get('tag').setValue(inputString);
    }

    public onBlur(): void {
        this.fileForm.get('tag').markAsTouched();
    }
}
